import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { CssBaseline, useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () => {
      const darkPalette = {
        page: "#0b1220",
        surface: "#111b2e",
        elevated: "#17233a",
        border: "rgba(148, 163, 184, 0.2)",
        textPrimary: "#e6edf8",
        textSecondary: "#a9b6cc",
        topbar: "#12213a",
        tabHeader: "#172740",
        input: "#131f34",
      };

      return createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
            },
          },
          typography: {
            fontFamily: [
              "Inter",
              "system-ui",
              "-apple-system",
              "BlinkMacSystemFont",
              "Segoe UI",
              "Roboto",
              "Helvetica Neue",
              "Arial",
              "sans-serif",
            ].join(","),
          },
          palette: {
            type: mode,
            primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : darkPalette.textPrimary },
            light: { main: mode === "light" ? "#F3F3F3" : darkPalette.elevated },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : darkPalette.tabHeader,
            optionsBackground: mode === "light" ? "#fafafa" : darkPalette.surface,
            fancyBackground: mode === "light" ? "#fafafa" : darkPalette.page,
            total: mode === "light" ? "#fff" : darkPalette.surface,
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : darkPalette.input,
            barraSuperior: mode === "light" ? primaryColorLight : darkPalette.topbar,
            background: {
              default: mode === "light" ? "#f8fafc" : darkPalette.page,
              paper: mode === "light" ? "#ffffff" : darkPalette.surface,
            },
            text: {
              primary: mode === "light" ? "#111827" : darkPalette.textPrimary,
              secondary: mode === "light" ? "#4b5563" : darkPalette.textSecondary,
            },
            divider: mode === "light" ? "rgba(15, 23, 42, 0.1)" : darkPalette.border,
            action: {
              hover: mode === "light" ? "rgba(15, 23, 42, 0.04)" : "rgba(148, 163, 184, 0.12)",
              selected: mode === "light" ? "rgba(37, 99, 235, 0.08)" : "rgba(56, 189, 248, 0.18)",
            },
          },
          overrides: {
            MuiCssBaseline: {
              "@global": {
                "html, body, #root": {
                  backgroundColor: mode === "light" ? "#f8fafc" : darkPalette.page,
                  color: mode === "light" ? "#111827" : darkPalette.textPrimary,
                },
                ".MuiPaper-root": {
                  backgroundImage: "none",
                },
                ".MuiInputBase-root, .MuiOutlinedInput-root, .MuiSelect-root": {
                  backgroundColor: mode === "light" ? "#ffffff" : darkPalette.input,
                  color: mode === "light" ? "#111827" : darkPalette.textPrimary,
                },
                ".MuiInputLabel-root, .MuiFormLabel-root": {
                  color: mode === "light" ? "#64748b" : darkPalette.textSecondary,
                },
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: mode === "light" ? "rgba(15, 23, 42, 0.16)" : darkPalette.border,
                },
                ".MuiTable-root": {
                  backgroundColor: mode === "light" ? "#ffffff" : darkPalette.surface,
                },
                ".MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: mode === "light" ? "rgba(15, 23, 42, 0.03)" : "rgba(148, 163, 184, 0.08)",
                  color: mode === "light" ? "#475569" : darkPalette.textSecondary,
                },
                ".MuiTableBody-root .MuiTableCell-root": {
                  color: mode === "light" ? "#111827" : darkPalette.textPrimary,
                },
                ".MuiTabs-root, .MuiTab-root": {
                  color: mode === "light" ? "#475569" : darkPalette.textSecondary,
                },
                ".recharts-cartesian-grid line": {
                  stroke: mode === "light" ? "#e2e8f0" : "rgba(148, 163, 184, 0.18)",
                },
                ".recharts-text": {
                  fill: mode === "light" ? "#64748b" : darkPalette.textSecondary,
                },
                "@media (max-width: 600px)": {
                  ".MuiTable-root": {
                    minWidth: "720px",
                  },
                  ".MuiTableContainer-root, .MuiPaper-root": {
                    maxWidth: "100%",
                  },
                  ".MuiButton-root": {
                    minHeight: 40,
                  },
                },
              },
            },
            MuiTooltip: {
              tooltip: {
                backgroundColor: mode === "light" ? "rgba(33, 43, 54, 0.92)" : darkPalette.elevated,
                color: mode === "light" ? "#ffffff" : darkPalette.textPrimary,
                fontSize: "0.72rem",
                fontWeight: 500,
                border: mode === "light" ? "none" : `1px solid ${darkPalette.border}`,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
              },
              arrow: {
                color: mode === "light" ? "rgba(33, 43, 54, 0.92)" : darkPalette.elevated,
              },
            },
            MuiDialog: {
              paper: {
                backgroundColor: mode === "light" ? "#ffffff" : darkPalette.surface,
                backgroundImage: "none",
              },
            },
            MuiPopover: {
              paper: {
                backgroundColor: mode === "light" ? "#ffffff" : darkPalette.surface,
                backgroundImage: "none",
              },
            },
            MuiMenu: {
              paper: {
                backgroundColor: mode === "light" ? "#ffffff" : darkPalette.surface,
              },
            },
            MuiTableCell: {
              root: {
                borderBottom: mode === "light"
                  ? "1px solid rgba(15, 23, 42, 0.08)"
                  : `1px solid ${darkPalette.border}`,
              },
            },
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      );
    },
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale = i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(file ? getBackendUrl() + "/public/" + file : defaultLogoLight);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(file ? getBackendUrl() + "/public/" + file : defaultLogoDark);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Whaticket - V15.0.0");
      })
      .catch((error) => {
        console.log("!==== Erro ao carregar temas: ====!", error);
        setAppName("Whaticket - V15.0.0");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = mode !== "light";
    root.setAttribute("data-theme", isDark ? "dark" : "light");
    root.style.setProperty("--primaryColor", isDark ? primaryColorDark : primaryColorLight);
    root.style.setProperty("--appBackground", isDark ? "#0b1220" : "#ffffff");
    root.style.setProperty("--pageBg", isDark ? "#0b1220" : "#f8fafc");
    root.style.setProperty("--paperBg", isDark ? "#111b2e" : "#ffffff");
    root.style.setProperty("--elevatedBg", isDark ? "#17233a" : "#ffffff");
    root.style.setProperty("--surfaceBg", isDark ? "#131f34" : "#f8fafc");
    root.style.setProperty("--hoverBg", isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(15, 23, 42, 0.04)");
    root.style.setProperty("--textPrimary", isDark ? "#e6edf8" : "#111827");
    root.style.setProperty("--textSecondary", isDark ? "#a9b6cc" : "#4b5563");
    root.style.setProperty("--textMuted", isDark ? "#7b8aa3" : "#94a3b8");
    root.style.setProperty("--borderSoft", isDark ? "rgba(148, 163, 184, 0.2)" : "#e7edf3");
    root.style.setProperty("--borderMuted", isDark ? "rgba(148, 163, 184, 0.14)" : "#edf2f7");
    root.style.setProperty("--chipBg", isDark ? "rgba(56, 189, 248, 0.12)" : "#EAF1FF");
    root.style.setProperty("--tabInactiveBg", isDark ? "#17233a" : "#ffffff");
    root.style.setProperty("--tabInactiveText", isDark ? "#a9b6cc" : "#64748b");
    root.style.setProperty("--messageMeOwn", isDark ? "#054640" : "#dcf8c6");
    root.style.setProperty("--messageThemBg", isDark ? "#1f2c3d" : "#f5f5f5");

    let globalStyle = document.getElementById("codex-dark-ui-overrides");
    if (!globalStyle) {
      globalStyle = document.createElement("style");
      globalStyle.id = "codex-dark-ui-overrides";
      document.head.appendChild(globalStyle);
    }
    globalStyle.textContent = isDark
      ? `
        html[data-theme="dark"] body {
          background: #0b1220 !important;
          color: #e6edf8 !important;
        }
        html[data-theme="dark"] body .MuiPaper-root,
        html[data-theme="dark"] body .MuiCard-root {
          background-color: #111b2e !important;
          color: #e6edf8 !important;
          background-image: none !important;
          border-color: rgba(148, 163, 184, 0.2) !important;
        }
        html[data-theme="dark"] body .MuiInputBase-root,
        html[data-theme="dark"] body .MuiOutlinedInput-root,
        html[data-theme="dark"] body .MuiTextField-root .MuiInputBase-root {
          background-color: #131f34 !important;
          color: #e6edf8 !important;
        }
        html[data-theme="dark"] body .MuiInputBase-input,
        html[data-theme="dark"] body input.MuiInputBase-input {
          background-color: #131f34 !important;
          color: #e6edf8 !important;
          -webkit-text-fill-color: #e6edf8 !important;
        }
        html[data-theme="dark"] body .MuiOutlinedInput-notchedOutline {
          border-color: rgba(148, 163, 184, 0.32) !important;
        }
        html[data-theme="dark"] body .MuiInputLabel-root,
        html[data-theme="dark"] body .MuiFormLabel-root {
          color: #a9b6cc !important;
        }
        html[data-theme="dark"] body .MuiTableCell-root {
          color: #e6edf8 !important;
          border-color: rgba(148, 163, 184, 0.18) !important;
        }
        html[data-theme="dark"] body .MuiTableHead-root .MuiTableCell-root {
          background-color: rgba(148, 163, 184, 0.08) !important;
          color: #cbd5e1 !important;
        }
        html[data-theme="dark"] body .MuiTypography-colorTextSecondary,
        html[data-theme="dark"] body .MuiTypography-colorTextSecondary.MuiTypography-root {
          color: #a9b6cc !important;
        }
        html[data-theme="dark"] body .MuiSvgIcon-root {
          color: inherit;
        }
      `
      : "";
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon ? appLogoFavicon : defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
  <div style={{ position: "relative", overflow: "visible", zIndex: 0, minHeight: "100vh" }}>
    <Routes />
  </div>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;

